import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowUpRight, ArrowDown, MapPin } from "lucide-react";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import PtfButton from "@/components/PtfButton";
import { Reveal, MaskText } from "@/components/motion/Reveal";
import { EvidenceChip } from "@/components/Evidence";
import { api } from "@/lib/api";
import { DAWN_CHALLENGES, DAWN_MODEL, DAWN_SDGS, DAWN_TIMELINE, DAWN_TARGETS } from "@/lib/content";

const Eyebrow = ({ children, light }) => (
  <p className={`text-[0.7rem] uppercase tracking-[0.28em] mb-5 flex items-center gap-3 ${light ? "text-ptf-accent" : "text-ptf-primary/60"}`}>
    <span className={`h-px w-8 inline-block ${light ? "bg-ptf-accent" : "bg-ptf-primary/40"}`} />
    {children}
  </p>
);

function WhatIsDawn() {
  return (
    <section data-testid="what-is-dawn" className="bg-ptf-bg py-20 md:py-32">
      <div className="mx-auto max-w-[1600px] px-5 sm:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
        <div className="lg:col-span-6">
          <Reveal><Eyebrow>What is Dawn?</Eyebrow></Reveal>
          <MaskText
            lines={["The beginning of", "twilight, before", "sunrise."]}
            className="font-display font-extrabold uppercase text-4xl sm:text-5xl lg:text-6xl leading-[0.92] tracking-tight text-ptf-primary"
          />
        </div>
        <div className="lg:col-span-6">
          <Reveal delay={0.15}>
            <p className="font-serif-ed italic text-xl text-ptf-secondary leading-snug">
              "The time that marks the beginning of twilight before sunrise for the
              communities living in remote locations of India, because what starts
              here will change the world."
            </p>
            <p className="mt-6 text-lg leading-relaxed text-ptf-text/80">
              Project Dawn began in 2019, on a recce to open a rafting route in a
              remote corner of Uttarakhand. What the team found instead were small
              communities living without proper roads, electricity or hospitals, and
              a need for hope from an outside world that hardly knew they existed.
              Three villages were adopted, and Dawn was born: rural development
              through responsible tourism, mixing eleven Sustainable Development
              Goals with outdoor and adventure sports.
            </p>
            <p className="mt-6 leading-relaxed text-ptf-text/70">
              It is not a charity model and it is not a holiday. Travellers, volunteers
              and athletes come into the field; the villages gain skills, income and
              infrastructure; and everything that happens is documented as it happens.
            </p>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

const CHALLENGE_PHOTOS = [
  { src: "/dawn/vor-water.jpg", caption: "Water drawn from an open spring" },
  { src: "/dawn/vor-sanitation.jpg", caption: "Village sanitation block" },
  { src: "/dawn/vor-school.jpg", caption: "An open-air class in the valley" },
  { src: "/dawn/vor-kitchen.jpg", caption: "Cooking on a wood fire" },
];

function Challenge() {
  return (
    <section data-testid="dawn-challenge" className="bg-ptf-primary text-ptf-bg py-20 md:py-32">
      <div className="mx-auto max-w-[1600px] px-5 sm:px-8 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <Reveal><Eyebrow light>The challenge</Eyebrow></Reveal>
            <MaskText
              lines={["What the field", "documented."]}
              className="font-display font-extrabold uppercase text-4xl sm:text-5xl lg:text-6xl leading-[0.92] tracking-tight"
            />
            <Reveal delay={0.15}>
              <p className="mt-6 text-ptf-bg/70 leading-relaxed max-w-md">
                Data gathered by adopting three villages in a remote location of
                Uttarakhand. These are documented field observations from specific
                communities, not generalisations about rural India.
              </p>
            </Reveal>
            <ul data-testid="challenge-list" className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
              {DAWN_CHALLENGES.map((c) => (
                <li key={c} className="flex items-start gap-2.5 text-sm text-ptf-bg/85 border-b border-ptf-bg/10 pb-3">
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 bg-ptf-accent" /> {c}
                </li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-7 grid grid-cols-2 gap-4">
            {CHALLENGE_PHOTOS.map((p, i) => (
              <Reveal key={p.src} delay={i * 0.08}>
                <figure>
                  <div className="aspect-[4/3] overflow-hidden">
                    <img src={p.src} alt={p.caption} className="h-full w-full object-cover" loading="lazy" />
                  </div>
                  <figcaption className="mt-2 text-[0.65rem] uppercase tracking-[0.12em] text-ptf-bg/55">{p.caption} · Field photograph</figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function Model() {
  return (
    <section data-testid="dawn-model" className="bg-ptf-bg py-20 md:py-32">
      <div className="mx-auto max-w-[1600px] px-5 sm:px-8 lg:px-12">
        <div className="max-w-3xl mb-14">
          <Reveal><Eyebrow>The model</Eyebrow></Reveal>
          <MaskText
            lines={["From understanding", "to opportunity."]}
            className="font-display font-extrabold uppercase text-4xl sm:text-5xl lg:text-6xl leading-[0.92] tracking-tight text-ptf-primary"
          />
          <Reveal delay={0.15}>
            <p className="mt-5 text-ptf-text/70 leading-relaxed">
              The Project Dawn approach. Not every stage has been implemented
              everywhere: the Valley of River pilot has carried it furthest.
            </p>
          </Reveal>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-10">
          {DAWN_MODEL.map((s, i) => (
            <Reveal key={s.num} delay={(i % 4) * 0.06}>
              <div className="relative border-t-2 border-ptf-primary/15 pt-5 h-full hover:border-ptf-accent transition-colors duration-300">
                <span className="font-display text-3xl text-ptf-primary/30">{s.num}</span>
                <h3 className="font-display uppercase text-xl tracking-tight text-ptf-primary mt-2 leading-none">{s.title}</h3>
                <p className="mt-3 text-sm text-ptf-text/70 leading-relaxed">{s.text}</p>
                {i < DAWN_MODEL.length - 1 && <ArrowDown className="lg:hidden h-4 w-4 text-ptf-primary/25 mt-4" />}
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

const CHAPTERS = [
  { to: "/dawn/valley-of-river", kicker: "Case study", title: "Valley of River", text: "A rural tourism pilot across three adopted villages in the Ladhiya Valley.", image: "/dawn/vor-river.jpg", chip: "PILOT" },
  { to: "/dawn/tree-plantation", kicker: "Field note · Feb 2024", title: "450 trees. One field day.", text: "A plantation drive with volunteers in Village Kuna, Tiuni.", image: "/dawn/trees-kids.jpg", chip: "DOCUMENTED" },
  { to: "/dawn/butt-to-board", kicker: "Innovation", title: "Butt to Board", text: "Cigarette butts collected across four cities, remade into a surfboard.", image: "/dawn/b2b-boards.jpg", chip: "DOCUMENTED" },
  { to: "/dawn/high-altitude", kicker: "Ongoing mission · Since 2019", title: "High altitude", text: "From the 2019 mission to Kilimanjaro's summit in 2023. The remaining summits await partners and sponsors.", image: "/dawn/kilimanjaro-summit.jpg", chip: "DOCUMENTED" },
  { to: "/dawn/film", kicker: "Film", title: "Dawn: a project of belief", text: "A documentary seeking collaboration, sponsorship and partners to reach film festivals, with Laxmi's story.", image: "/dawn/vor-kitchen.jpg", chip: "PLANNED" },
  { to: "/dawn/laxmi", kicker: "Field story", title: "Laxmi's story", text: "Laxmi Negi's documented account from Khirdwari: thirteen river crossings from the road, seventy kilometres from a hospital.", image: "/dawn/laxmi-portrait.jpg", chip: "DOCUMENTED" },
];

function Chapters() {
  return (
    <section data-testid="dawn-chapters" className="bg-ptf-bg py-20 md:py-32">
      <div className="mx-auto max-w-[1600px] px-5 sm:px-8 lg:px-12">
        <div className="max-w-3xl mb-14">
          <Reveal><Eyebrow>The chapters</Eyebrow></Reveal>
          <MaskText
            lines={["Dawn, in the field."]}
            className="font-display font-extrabold uppercase text-4xl sm:text-5xl lg:text-6xl leading-[0.92] tracking-tight text-ptf-primary"
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {CHAPTERS.map((c, i) => (
            <Reveal key={c.to} delay={(i % 3) * 0.07}>
              <Link to={c.to} data-testid={`chapter-${c.to.split("/").pop()}`} className="group block h-full border border-ptf-primary/12 bg-white hover:border-ptf-primary/30 transition-colors">
                <div className="relative aspect-[16/10] overflow-hidden">
                  <img src={c.image} alt={c.title} className="h-full w-full object-cover transition-transform duration-[1.2s] group-hover:scale-105" loading="lazy" />
                  <div className="absolute top-3 left-3"><EvidenceChip status={c.chip} /></div>
                </div>
                <div className="p-6">
                  <p className="text-[0.6rem] uppercase tracking-[0.2em] text-ptf-secondary">{c.kicker}</p>
                  <h3 className="mt-2 font-display uppercase text-2xl tracking-tight text-ptf-primary leading-none group-hover:text-ptf-secondary transition-colors">{c.title}</h3>
                  <p className="mt-3 text-sm text-ptf-text/70 leading-relaxed">{c.text}</p>
                  <span className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.14em] text-ptf-primary">Read <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" /></span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Interventions() {
  const { data: metrics = [] } = useQuery({ queryKey: ["impact"], queryFn: async () => (await api.get("/impact")).data });
  const dawn = metrics.filter((m) => (m.programme || "").toLowerCase().includes("dawn") || (m.programme || "").toLowerCase().includes("valley"));
  if (dawn.length === 0) return null;
  return (
    <section data-testid="dawn-interventions" className="bg-ptf-primary text-ptf-bg py-20 md:py-32 grain">
      <div className="mx-auto max-w-[1600px] px-5 sm:px-8 lg:px-12">
        <div className="max-w-3xl mb-12">
          <Reveal><Eyebrow light>Documented outcomes</Eyebrow></Reveal>
          <MaskText
            lines={["Documented field", "interventions."]}
            className="font-display font-extrabold uppercase text-4xl sm:text-5xl leading-[0.92] tracking-tight"
          />
          <Reveal delay={0.15}>
            <p className="mt-5 text-ptf-bg/70 leading-relaxed">
              Each entry carries its location, date, programme and source. They are
              field interventions, recorded as they happened, not impact claims.
            </p>
          </Reveal>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-px bg-ptf-bg/10 border border-ptf-bg/10">
          {dawn.map((m, i) => (
            <Reveal key={m.id} delay={(i % 4) * 0.05} className="h-full">
              <div className="bg-ptf-primary p-6 h-full flex flex-col">
                <p className="font-display text-4xl md:text-5xl text-ptf-accent leading-none">{m.value}<span className="text-lg ml-1.5 text-ptf-bg/60 font-sans lowercase tracking-normal">{m.unit}</span></p>
                <p className="mt-3 text-sm text-ptf-bg/85 leading-snug flex-1">{m.label}</p>
                <p className="mt-4 text-[0.62rem] uppercase tracking-[0.12em] text-ptf-bg/45 leading-relaxed">
                  {[m.location, m.date].filter(Boolean).join(" · ")}
                </p>
              </div>
            </Reveal>
          ))}
        </div>
        <div className="mt-8"><PtfButton to="/impact" variant="accent" data-testid="dawn-to-impact">Full Evidence Record</PtfButton></div>
      </div>
    </section>
  );
}

function SdgFramework() {
  return (
    <section data-testid="dawn-sdgs" className="bg-ptf-bg py-20 md:py-32">
      <div className="mx-auto max-w-[1600px] px-5 sm:px-8 lg:px-12">
        <div className="max-w-3xl mb-12">
          <Reveal><Eyebrow>Our development framework</Eyebrow></Reveal>
          <MaskText
            lines={["Eleven goals,", "one valley at a time."]}
            className="font-display font-extrabold uppercase text-4xl sm:text-5xl leading-[0.92] tracking-tight text-ptf-primary"
          />
          <Reveal delay={0.15}>
            <p className="mt-5 text-ptf-text/70 leading-relaxed">
              Project Dawn works with eleven of the seventeen Sustainable Development
              Goals. Each connection below is drawn from documented field work only.
            </p>
          </Reveal>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-ptf-primary/10 border border-ptf-primary/10">
          {DAWN_SDGS.map((s, i) => (
            <Reveal key={s.num} delay={(i % 3) * 0.05} className="h-full">
              <div className="bg-ptf-bg p-6 h-full">
                <div className="flex items-baseline gap-3">
                  <span className="font-display text-3xl text-ptf-primary/25">{String(s.num).padStart(2, "0")}</span>
                  <h3 className="font-display uppercase text-lg tracking-tight text-ptf-primary leading-none">{s.title}</h3>
                </div>
                <p className="mt-3 text-sm text-ptf-text/70 leading-relaxed">{s.link}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function EarlierVision() {
  return (
    <section data-testid="earlier-vision" className="bg-white border-y border-ptf-primary/10 py-16 md:py-24">
      <div className="mx-auto max-w-[1600px] px-5 sm:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5">
          <Reveal><Eyebrow>Earlier vision</Eyebrow></Reveal>
          <h2 className="font-display font-extrabold uppercase text-3xl md:text-4xl tracking-tight text-ptf-primary leading-[0.95]">What Dawn once set out to reach.</h2>
          <p className="mt-5 text-ptf-text/70 leading-relaxed text-sm">
            The original Project Dawn material framed these ambitions as targets by
            2025. They are preserved here as historical targets, not as achievements.
            Current progress will replace them as it is verified.
          </p>
        </div>
        <div className="lg:col-span-7 grid grid-cols-2 sm:grid-cols-3 gap-4">
          {DAWN_TARGETS.map((t) => (
            <div key={t} className="border border-ptf-primary/12 p-5">
              <EvidenceChip status="HISTORICAL TARGET" />
              <p className="mt-3 font-display uppercase text-xl tracking-tight text-ptf-primary/70">{t}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Journey() {
  return (
    <section data-testid="dawn-journey" className="bg-ptf-bg py-20 md:py-32">
      <div className="mx-auto max-w-[1200px] px-5 sm:px-8 lg:px-12">
        <div className="max-w-3xl mb-14">
          <Reveal><Eyebrow>Our journey</Eyebrow></Reveal>
          <MaskText
            lines={["A longer story."]}
            className="font-display font-extrabold uppercase text-4xl sm:text-5xl leading-[0.92] tracking-tight text-ptf-primary"
          />
        </div>
        <div className="relative border-l-2 border-ptf-primary/15 pl-8 md:pl-12 space-y-12">
          {DAWN_TIMELINE.map((t, i) => (
            <Reveal key={t.title} delay={i * 0.05}>
              <div className="relative">
                <span className="absolute -left-[41px] md:-left-[57px] top-1.5 h-3 w-3 rounded-full bg-ptf-accent border-2 border-ptf-primary" />
                <p className="text-[0.65rem] uppercase tracking-[0.22em] text-ptf-secondary">{t.period}</p>
                <h3 className="mt-1.5 font-display uppercase text-2xl tracking-tight text-ptf-primary leading-none">{t.title}</h3>
                <p className="mt-3 text-ptf-text/70 leading-relaxed max-w-2xl">{t.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function ProjectDawn() {
  return (
    <>
      <Seo title="Project Dawn" path="/dawn" description="Project Dawn is a Planet Trotter initiative connecting rural development, responsible tourism, outdoor and adventure sports, education and sustainable development in Uttarakhand." />
      <PageHero
        eyebrow="Project Dawn"
        titleLines={["Adventure.", "Community.", "Opportunity."]}
        intro="A Planet Trotter initiative connecting rural development, responsible tourism, outdoor and adventure sports, education and sustainable development, documented from the field."
        image="/dawn/vor-aerial.jpg"
      />
      <WhatIsDawn />
      <Challenge />
      <Model />
      <Chapters />
      <Interventions />
      <SdgFramework />
      <EarlierVision />
      <Journey />
      <section className="bg-ptf-secondary text-ptf-bg py-20 md:py-28">
        <div className="mx-auto max-w-[1600px] px-5 sm:px-8 lg:px-12 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
          <h2 className="font-display font-extrabold uppercase text-4xl md:text-6xl tracking-tight leading-[0.95]">Bring Dawn to the<br />next valley.</h2>
          <div className="flex flex-col sm:flex-row gap-3">
            <PtfButton to="/partnerships" variant="accent" data-testid="dawn-partner-cta">Start a Conversation</PtfButton>
            <PtfButton to="/volunteer" variant="outlineLight">Volunteer in the Field</PtfButton>
          </div>
        </div>
      </section>
    </>
  );
}
